/**
 * WHAT DIES ON THIS DEVICE WHEN A NEW BUILD ARRIVES.
 *
 * App.vue clears a few localStorage keys the first time it boots under a new
 * build number. The rule used to be the whole `ssi-script-` prefix, written
 * when that prefix meant one thing: the LEARNING SCRIPT, cached as
 * `ssi-script-v12:<course>` before it moved into IndexedDB. Script MODE then
 * took the same prefix for its own marks — the calibration, the letters
 * already introduced, the parts already celebrated, the ladder mirror — and
 * every deploy quietly wiped them. Tom, 2026-09-22: "Aran just did that
 * update. It made him do the dots again, the screen writing calibration
 * thing."
 *
 * So the rule is the SHAPE of the dead cache, not the prefix it happened to
 * share: `ssi-script-v<n>:<course>`, a versioned script blob and nothing else.
 * Script mode's own keys carry a word where the version sits
 * (`ssi-script-calibrate-done:`, `ssi-script-progress:v2:…`) and survive, the
 * way a learner's position already does.
 */

/** A learning-script blob from the days those lived in localStorage. */
const isLegacyScriptCache = (key: string): boolean => /^ssi-script-v\d+:/.test(key)

/** The belt key that predates the build-number stamp. */
const isStaleVersionKey = (key: string): boolean => key === 'ssi_app_version'

/** Every localStorage key a build change may remove. Pure: reads, never writes. */
export function staleBuildKeys(storage: Pick<Storage, 'length' | 'key'>): string[] {
  const out: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (!key) continue
    if (isLegacyScriptCache(key) || isStaleVersionKey(key)) out.push(key)
  }
  return out
}
